import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StyleSheet, View, Text } from './components/base_components';
import { BusyOverlay, useBusy } from './components/busy_component';
import PropList from './components/prop_list';
import StandardBoxList from './components/standard_box_list';
import TextButton from './components/buttons/text_button';

import UserStore from './stores/user_store';
import UserAdminStore from './stores/user_admin_store';

const styles = StyleSheet.create({
  userListScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  text: {
    marginLeft: '2rem',
    marginTop: '1.5em',
    fontSize: '1.5em',
  },
  button: {
    marginLeft: '1rem',
  },
  bottomLine: {
    alignSelf: 'stretch',
    height: '5rem',
    backgroundColor: 'var(--box-header-bg-color)',
    borderTopWidth: 1,
    borderColor: 'var(--box-inner-line-color)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
});

export default function UserListScreen() {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const org = UserStore.useOrg();
  const list = UserAdminStore.useList(org?.org_id);
  const [email, setEmail] = React.useState('');
  const [is_admin, setAdmin] = React.useState(false);
  const [cap_add_device, setAddDevice] = React.useState(true);
  const [cap_web_download, setWebDownload] = React.useState(true);
  const [cap_web_upload, setWebUpload] = React.useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    UserAdminStore.fetch(org?.org_id);
  }, [org?.org_id]);

  function _onAddUser() {
    if (email && setBusy()) {
      const opts = {
        org_id: org.org_id,
        email,
        is_admin,
        cap_add_device: is_admin || cap_add_device,
        cap_web_download: is_admin || cap_web_download,
        cap_web_upload: is_admin || cap_web_upload,
      };
      UserAdminStore.addUser(opts, (err, user_id) => {
        clearBusy();
        if (err === 402) {
          window.alert('Sorry, your plan limit for users has been reached.');
        } else if (err === 409) {
          window.alert('User already exists');
        } else if (err) {
          window.alert('Failed to add user.');
        } else {
          navigate(`/admin/user/${user_id}`);
        }
      });
    }
  }
  const rows = list?.map?.((user: any) => {
    const perms = user.is_admin
      ? 'Org Admin'
      : [
          user.cap_web_upload ? 'Web Download' : '',
          user.cap_web_download ? 'Web Upload' : '',
          user.cap_add_device ? 'Add Devices' : '',
        ]
          .filter((p) => p)
          .join(', ') || 'Usage';
    return {
      url: '/admin/user/' + user?.user_id,
      list: [user.email, user.user_name || '-', perms],
    };
  });

  return (
    <View style={styles.userListScreen}>
      <StandardBoxList
        title='Users'
        detail={list?.length}
        headers={['Email', 'User Name', 'Permissions']}
        sizes={[2, 1, 2]}
        rows={rows}
      />
      <StandardBoxList title='Add User'>
        <Text style={styles.text}>Add a new user to your organization</Text>
        <PropList
          editing={true}
          columns={[
            [
              {
                key: 'email',
                label: 'Email',
                value: email,
                input: {
                  placeholder: 'Email Address',
                  onChangeText: setEmail,
                },
              },
              {
                key: 'is_admin',
                label: 'Role',
                value: is_admin,
                radio: {
                  options: [
                    { text: 'Admin', value: true },
                    { text: 'Member', value: false },
                  ],
                  onChange: setAdmin,
                },
              },
              {
                key: 'cap_web_download',
                label: 'Web Download',
                value: cap_web_download || is_admin,
                radio: {
                  disabled: is_admin,
                  options: [
                    { text: 'Enabled', value: true },
                    { text: 'Disabled', value: false },
                  ],
                  onChange: setWebDownload,
                },
              },
              {
                key: 'cap_web_upload',
                label: 'Web Upload',
                value: cap_web_upload || is_admin,
                radio: {
                  disabled: is_admin,
                  options: [
                    { text: 'Enabled', value: true },
                    { text: 'Disabled', value: false },
                  ],
                  onChange: setWebUpload,
                },
              },
              {
                key: 'cap_add_device',
                label: 'Device Add Permission',
                value: cap_add_device || is_admin,
                radio: {
                  disabled: is_admin,
                  options: [
                    { text: 'Enabled', value: true },
                    { text: 'Disabled', value: false },
                  ],
                  onChange: setAddDevice,
                },
              },
            ],
          ]}
        />
        <View style={styles.bottomLine}>
          <TextButton
            style={styles.button}
            text='Add User'
            onPress={_onAddUser}
          />
        </View>
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
